import { AnimatePresence, motion, type Variants } from "framer-motion";
import { type PropsWithChildren, useRef } from "react";
import { type AriaModalOverlayProps, DismissButton, FocusScope, Overlay, useModalOverlay } from "react-aria";
import type { OverlayTriggerState } from "react-stately";

import style from "./Modal.module.css";

export interface ModalProps extends AriaModalOverlayProps {
  state: OverlayTriggerState;
}

export const Modal = ({ state, children, ...props }: PropsWithChildren<ModalProps>) => {
  const ref = useRef(null);
  const { modalProps, underlayProps } = useModalOverlay(props, state, ref);

  const variants: Variants = {
    initial: { opacity: 0, scale: 1.05 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, scale: 0.98 },
  };

  return (
    <AnimatePresence>
      {state.isOpen && (
        <Overlay key="modal">
          <div {...underlayProps} className={style.modal} data-testid="modal-overlay">
            <motion.div
              className={style.underlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <div {...modalProps} ref={ref}>
              <motion.div
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
                className={style.container}
              >
                <FocusScope contain restoreFocus autoFocus>
                  <DismissButton onDismiss={state.close} />
                  {children}
                  <DismissButton onDismiss={state.close} />
                </FocusScope>
              </motion.div>
            </div>
          </div>
        </Overlay>
      )}
    </AnimatePresence>
  );
};
